/**
 * EffectRegistry — known effect types + their factory functions.
 *
 * Used by the dynamic EffectsChain so the user can add any number of
 * effect instances (multiple distortions, two delays, whatever) at
 * runtime via the EFFECTS palette. Each descriptor knows:
 *
 *   - `type`         Module type string. Stable on the wire. Matches
 *                    the SynthModule.type and is what presets store.
 *   - `displayName`  Short label (also rendered as the panel title).
 *   - `create(id?)`  Mint a fresh instance. When `id` is supplied (preset
 *                    load, migration), the returned module uses it
 *                    verbatim; otherwise the module mints a unique id.
 *
 * The registry is the single source of truth for "what effect types
 * exist" — UI palettes, randomizer (when it grows the add/remove
 * surface), and migrations all read from it instead of hard-coding
 * the list. Adding a new effect = registering a descriptor here, and
 * the rest of the app inherits the new type for free.
 */

import { BitcrusherModule } from '../modules/Bitcrusher';
import { ChorusModule } from '../modules/Chorus';
import { CompressorModule } from '../modules/Compressor';
import { DelayModule } from '../modules/Delay';
import { DistortionModule } from '../modules/Distortion';
import { EQModule } from '../modules/EQ';
import { FlangerModule } from '../modules/Flanger';
import { GranularModule } from '../modules/Granular';
import { PhaserModule } from '../modules/Phaser';
import { ReverbModule } from '../modules/Reverb';
import { RingModModule } from '../modules/RingMod';
import { RuinModule } from '../modules/Ruin';
import { WavefolderModule } from '../modules/Wavefolder';
import type { Transport } from '../Transport';
import type { EffectModule } from '../EffectsChain';
import type { SynthModule } from '../modules/SynthModule';

export interface EffectDescriptor {
  /** Stable module type (matches SynthModule.type). */
  readonly type: string;
  /** Short rack-header label. */
  readonly displayName: string;
  /** Factory — `id` is optional; module mints one when omitted. */
  create(id?: string): EffectModule;
}

/**
 * EffectRegistry maps `type` → descriptor and offers a single
 * `create(type, id?)` entry point for the chain and migrations. Lookup
 * is O(1); list ordering follows registration order so the palette
 * UI is stable.
 */
export class EffectRegistry {
  private readonly descriptors = new Map<string, EffectDescriptor>();
  /** Preserves registration order so list() is stable for the palette UI. */
  private readonly order: string[] = [];

  register(desc: EffectDescriptor): void {
    if (this.descriptors.has(desc.type)) return;
    this.descriptors.set(desc.type, desc);
    this.order.push(desc.type);
  }

  list(): readonly EffectDescriptor[] {
    return this.order.map((t) => this.descriptors.get(t)!).filter(Boolean);
  }

  get(type: string): EffectDescriptor | undefined {
    return this.descriptors.get(type);
  }

  has(type: string): boolean {
    return this.descriptors.has(type);
  }

  /**
   * Create a fresh effect instance of `type`. `id` is forwarded to the
   * module's constructor — preset load uses this so old connections
   * referencing the saved id still find their target. Returns null if
   * the type is unknown.
   */
  create(type: string, id?: string): EffectModule | null {
    const d = this.descriptors.get(type);
    if (!d) return null;
    return d.create(id);
  }
}

/**
 * The default registry, populated with every effect type the synth
 * ships. Order here matters — palette buttons render in this sequence
 * and the bootstrap chain pulls its initial composition from this list
 * too. Group flow: dynamics (COMP) → coloration (DIST, RING) →
 * modulation (CHORUS, FLANGE, PHASER) → time (DELAY, REVERB) →
 * digital character (CRUSH).
 */
export function defaultEffectRegistry(transport: Transport): EffectRegistry {
  const r = new EffectRegistry();
  // EffectModule narrows input/output to non-null AudioNode, but each
  // module's input/output is nullable until init() runs (we hand back
  // a real AudioNode every time after that). The cast lets the registry
  // adopt the wider SynthModule shape and the chain treats them as
  // EffectModule from the moment they're added (always after init).
  const adopt = (m: SynthModule): EffectModule => m as unknown as EffectModule;

  r.register({
    type: 'compressor',
    displayName: 'COMP',
    create: (id) => adopt(new CompressorModule(id)),
  });
  r.register({
    type: 'eq',
    displayName: 'EQ',
    create: (id) => adopt(new EQModule(id)),
  });
  r.register({
    type: 'distortion',
    displayName: 'DIST',
    create: (id) => adopt(new DistortionModule(id)),
  });
  r.register({
    type: 'wavefolder',
    displayName: 'FOLD',
    create: (id) => adopt(new WavefolderModule(id)),
  });
  r.register({
    type: 'ringmod',
    displayName: 'RING',
    create: (id) => adopt(new RingModModule(id)),
  });
  r.register({
    type: 'ruin',
    displayName: 'RUIN',
    create: (id) => adopt(new RuinModule(id)),
  });
  r.register({
    type: 'chorus',
    displayName: 'CHORUS',
    create: (id) => adopt(new ChorusModule(id)),
  });
  r.register({
    type: 'flanger',
    displayName: 'FLANGE',
    create: (id) => adopt(new FlangerModule(id)),
  });
  r.register({
    type: 'phaser',
    displayName: 'PHASER',
    create: (id) => adopt(new PhaserModule(id)),
  });
  r.register({
    type: 'delay',
    displayName: 'DELAY',
    // Delay needs the transport for sync mode. Bound here so the
    // factory signature stays uniform across types.
    create: (id) => adopt(new DelayModule(id, transport)),
  });
  r.register({
    type: 'reverb',
    displayName: 'REVERB',
    create: (id) => adopt(new ReverbModule(id)),
  });
  r.register({
    type: 'bitcrusher',
    displayName: 'CRUSH',
    create: (id) => adopt(new BitcrusherModule(id)),
  });
  r.register({
    // Granular delay — circular-buffer grain processor with feedback,
    // pitch-per-grain, and stereo spread. Lives next to the other
    // texture-leaning effects (RUIN, FOLD, CRUSH) since it's the
    // same kind of "transform a signal into something weirder" tool.
    type: 'granular',
    displayName: 'GRAIN',
    create: (id) => adopt(new GranularModule(id)),
  });
  return r;
}
